import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import type { PapiDeviation, PapiOriginalsResponse } from '@wix/da-papi-types';
import { Url } from '@wix/da-url';
import {
  BiContextProvidedValue,
  BiData,
  MessageIconClickBiEvent,
  OriginalViewOrderRequestClickBiEvent,
  StudioSubmitOriginalDeviationBiEvent,
} from '@wix/da-bi/pkg/events';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { pushSubmitModal } from '@wix/da-submit-shared/pkg/redux/actions';
import UserAvatar from '@wix/da-shared-react/pkg/User/Avatar/UserAvatar';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import Timestamp from '@wix/da-shared-react/pkg/Timestamp/Timestamp';
import DeviationLink from '@wix/da-shared-react/pkg/Duperbrowse/DeviationLink/DeviationLink';
import UserLink from '@wix/da-shared-react/pkg/User/Link/UserLink';
import { usePushModal } from '@wix/da-shared-react/pkg/utils/hooks/usePushModal';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import MailIcon from '@wix/da-ds/pkg/Icons/24x24/MailOutline';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import ImageIcon from '@wix/da-ds/pkg/Icons/16x16/Image';
import OrderStatusDisplay from '../../OrderStatusDisplay';

import s from './OrdersListItem.scss';

interface Props {
  parentDeviation?: PapiDeviation;
  order: PapiOriginalsResponse;
  showTitle?: boolean;
  showStatus?: boolean;
}

const OrdersListItem: React.FC<Props> = ({
  parentDeviation,
  order,
  showTitle,
  showStatus,
}) => {
  const {
    user: requestingUser,
    status,
    acceptedTs,
    deliveredTs,
    paidTs,
    rejectedTs,
    deliveryDeviationid,
    deliveryStashPrivateid,
  } = order;
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const deviation = order.deviation ?? parentDeviation;
  const basePrice = deviation?.originals?.basePrice;
  const displayPrice = order.totalPrice ?? basePrice ?? 0;

  const openOriginalsOrderDetailsModal = usePushModal(
    ModalType.STUDIO_ORIGINALS_ORDER_DETAILS
  );

  const dispatchPushSubmitModal = useAction(pushSubmitModal);

  const sendMessageBiData = BiData<MessageIconClickBiEvent>({
    evid: 470,
    recipientid: requestingUser?.useridUuid ?? '',
    sectionname: 'originals',
  });

  const sendMessageIconButton = (
    <IconButton
      aria-label={t('studio.orders.list.actions.sendMessage')}
      icon={MailIcon}
      size="medium"
      tooltipText={t('studio.orders.list.actions.sendMessage')}
      href={Url.userMessageLink(requestingUser?.username ?? '')}
      biData={sendMessageBiData}
    />
  );

  const sendMessageSolidButton = (
    <SolidButton
      variant="main"
      size="medium"
      priority="secondary"
      icon={MailIcon}
      href={Url.userMessageLink(requestingUser?.username ?? '')}
      biData={sendMessageBiData}
    >
      {t('studio.orders.list.actions.sendMessage')}
    </SolidButton>
  );

  return (
    <div className={s['root']}>
      <div className={s['title-and-user']}>
        {showTitle && (
          <DeviationLink className={s['title']} deviation={deviation}>
            {deviation?.title}
          </DeviationLink>
        )}
        {requestingUser && (
          <div className={s['user']}>
            <UserAvatar user={requestingUser} size={24} />
            <UserLink user={requestingUser} usernameClassName={s['username']} />
          </div>
        )}
      </div>
      <div className={s['status-and-timestamp']}>
        {showStatus && (
          <div className={s['status']}>
            <OrderStatusDisplay status={status} />
          </div>
        )}
        <div className={s['updated-at']}>
          {status === 'pending' &&
            paidTs &&
            t('studio.orders.list.timestamp.pending', {
              postProcess: ['reactPostprocessor', 'featureBrancher'],
              timestamp: (
                <Timestamp timestamp={paidTs} showFullLengthOnMobile />
              ),
            })}
          {status === 'in_progress' &&
            acceptedTs &&
            t('studio.orders.list.timestamp.in_progress', {
              postProcess: ['reactPostprocessor', 'featureBrancher'],
              timestamp: (
                <Timestamp timestamp={acceptedTs} showFullLengthOnMobile />
              ),
            })}
          {status === 'completed' &&
            deliveredTs &&
            t('studio.orders.list.timestamp.completed', {
              postProcess: ['reactPostprocessor', 'featureBrancher'],
              timestamp: (
                <Timestamp timestamp={deliveredTs} showFullLengthOnMobile />
              ),
            })}
          {status === 'rejected' &&
            rejectedTs &&
            t('studio.orders.list.timestamp.rejected', {
              postProcess: ['reactPostprocessor', 'featureBrancher'],
              timestamp: (
                <Timestamp timestamp={rejectedTs} showFullLengthOnMobile />
              ),
            })}
        </div>
      </div>
      <div className={s['price']}>
        {t('studio.orders.list.price', {
          dollarPrice: formatDollars(displayPrice),
        })}
      </div>
      <div className={s['summary']}>
        {status !== 'pending' && (
          <PlainButtonOrLink
            className={s['summary-button']}
            onClick={() =>
              openOriginalsOrderDetailsModal({
                deviationId: deviation?.deviationId ?? 0,
                responseId: order.responseId,
              })
            }
          >
            {t('studio.orders.list.actions.orderSummary')}
          </PlainButtonOrLink>
        )}
      </div>
      <div className={s['actions']}>
        {status !== 'rejected' &&
          (isMobile ? sendMessageSolidButton : sendMessageIconButton)}
        {status === 'pending' && (
          <SolidButton
            variant="main"
            size="medium"
            priority="secondary"
            onClick={() =>
              openOriginalsOrderDetailsModal({
                deviationId: deviation?.deviationId ?? 0,
                responseId: order.responseId,
              })
            }
            biData={BiData<OriginalViewOrderRequestClickBiEvent>({
              evid: 772,
              responseid: order.responseId,
              recipientid: requestingUser?.useridUuid,
              typeid: deviation?.typeId,
              itemid: deviation?.deviationId,
              component: BiContextProvidedValue,
              view: BiContextProvidedValue,
            })}
          >
            {t('studio.orders.list.actions.viewOrder')}
          </SolidButton>
        )}
        {status === 'in_progress' && !deliveryDeviationid && (
          <SolidButton
            variant="main"
            size="medium"
            priority="secondary"
            onClick={() =>
              dispatchPushSubmitModal({
                originalsDeviationId: deviation?.deviationId,
                originalsResponseId: order.responseId,
              })
            }
            biData={BiData<StudioSubmitOriginalDeviationBiEvent>({
              evid: 776,
              itemid: deviation?.deviationId,
              typeid: deviation?.typeId,
              responseid: order.responseId,
              recipientid: requestingUser?.useridUuid,
            })}
          >
            {t('studio.orders.list.actions.submit')}
          </SolidButton>
        )}
        {status === 'in_progress' && deliveryDeviationid && (
          <SolidButton
            icon={ImageIcon}
            variant="main"
            size="medium"
            priority="secondary"
            onClick={() =>
              dispatchPushSubmitModal({
                deviationId: deliveryDeviationid,
                originalsDeviationId: deviation?.deviationId,
                originalsResponseId: order.responseId,
              })
            }
          >
            {t('studio.orders.list.actions.edit')}
          </SolidButton>
        )}
        {status === 'completed' && (
          <SolidButton
            variant="main"
            size="medium"
            priority="secondary"
            href={
              deliveryStashPrivateid
                ? Url.stashItemLink(deliveryStashPrivateid.toString(36))
                : Url.deviationLink(
                    deliveryDeviationid,
                    deviation?.author?.username
                  )
            }
          >
            {t('studio.orders.list.actions.viewOriginal')}
          </SolidButton>
        )}
        {status === 'rejected' && sendMessageSolidButton}
      </div>
    </div>
  );
};
OrdersListItem.displayName = 'OrdersListItem';

export default OrdersListItem;
